import React, { useState } from 'react';
import { ChevronDown, ExternalLink } from 'lucide-react';
import ReferencePageBadge from './ReferencePageBadge';
import { SEV_CONFIG, typeLabel } from '../utils/technicalErrors';
import { effortInfo } from '../utils/auditDictionary';

/**
 * Problèmes présents sur plusieurs pages (ex. en-têtes de sécurité).
 */
export default function TechnicalErrorsGrouped({ groups }) {
  const [openKeys, setOpenKeys] = useState(() => new Set());

  if (!groups?.length) return null;

  const toggle = (key) => {
    setOpenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className="space-y-2">
      {groups.map((g) => {
        const cfg = SEV_CONFIG[g.severity] || SEV_CONFIG.low;
        const eff = effortInfo(g.translation?.effort);
        const isOpen = openKeys.has(g.key);
        const previewUrls = g.urls.slice(0, 5);
        const more = g.urls.length - previewUrls.length;

        return (
          <div
            key={g.key}
            className="rounded-xl border border-blue-gray-200 bg-white overflow-hidden"
          >
            <button
              type="button"
              onClick={() => toggle(g.key)}
              className="w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-blue-gray-50/50 transition"
            >
              <ChevronDown
                size={18}
                className={`text-blue-gray-400 flex-shrink-0 mt-0.5 transition-transform ${isOpen ? 'rotate-180' : ''}`}
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${cfg.badge}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                    {cfg.label}
                  </span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-blue-gray-100 text-blue-gray-600">
                    {typeLabel(g.type)}
                  </span>
                  <span className="text-xs font-medium text-accent ml-auto">
                    {g.pageCount} page{g.pageCount > 1 ? 's' : ''}
                  </span>
                </div>
                <p className="font-medium text-blue-gray-900 text-sm">{g.category}</p>
                {g.translation?.pourquoi && (
                  <p className="text-xs text-blue-gray-500 mt-0.5 line-clamp-2">{g.translation.pourquoi}</p>
                )}
              </div>
            </button>

            {isOpen && (
              <div className="px-4 pb-4 pt-0 border-t border-blue-gray-100">
                {(g.recommendation || g.translation?.corriger) && (
                  <p className="text-xs text-blue-gray-600 mb-3">
                    <span className="font-medium text-blue-gray-700">À corriger : </span>
                    {g.recommendation || g.translation.corriger}
                  </p>
                )}
                <span className="inline-block text-[10px] font-medium text-blue-gray-500 bg-blue-gray-50 border border-blue-gray-200 px-1.5 py-0.5 rounded mb-2">
                  Effort : {eff.label}
                </span>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-blue-gray-400 mb-1.5">
                  Pages concernées
                </p>
                <ul className="space-y-1 max-h-48 overflow-y-auto custom-scrollbar">
                  {g.urls.map((url) => {
                    const short = url.replace(/^https?:\/\//, '');
                    const isRef = g.rows.some((r) => r.url === url && r.isReference);
                    return (
                      <li key={url} className="flex items-center gap-2 text-sm min-w-0">
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-accent hover:underline truncate flex items-center gap-1 min-w-0"
                          title={url}
                        >
                          <ExternalLink size={11} className="shrink-0" />
                          <span className="truncate">{short}</span>
                        </a>
                        {isRef && <ReferencePageBadge compact />}
                      </li>
                    );
                  })}
                </ul>
                {!isOpen && more > 0 && null}
              </div>
            )}

            {!isOpen && previewUrls.length > 0 && (
              <div className="px-4 pb-3 -mt-1">
                <p className="text-[11px] text-blue-gray-400 truncate">
                  {previewUrls.map((u) => u.replace(/^https?:\/\//, '')).join(' · ')}
                  {more > 0 ? ` · +${more}` : ''}
                </p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
